import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, TouchableOpacity, View, TextInput, ActivityIndicator, Image as RNImage } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import DebtBadge from '../../../../components/DebtBadge';
import api from '../../../../utils/api';
import { Icon } from '../../../../components/Icon';
import { Illustration } from '../../../../components/Illustration';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import useDebouncedValue from '@/hooks/useDebouncedValue';

import NewTicketModal from './NewTicketModal.web';
import TicketDetailModal from './TicketDetailModal.web';
import AreaEditModal from './AreaEditModal.web';
import CategoryEditModal from './CategoryEditModal.web';
import TicketFiltersBar, { EMPTY_FILTERS, TicketFilters, filtersToParams } from './TicketFilters.web';
import TicketsReport, { ExcelButton } from './TicketsReport.web';


type ViewMode = 'tickets' | 'areas' | 'categories' | 'reports';

const PER_PAGE = 30;

export default function TicketsDashboard() {
  const { communityId, can } = useActiveAdminCommunity();
  const queryClient = useQueryClient();

  const canManage = can('tickets.manage');
  // Calle, vivienda y reportes exponen datos de toda la comunidad.
  const canSeeCommunity = can('tickets.view', 'tickets.manage');

  const [view, setView] = useState<ViewMode>('tickets');
  const [filters, setFilters] = useState<TicketFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(filters.search.trim(), 300);

  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicketUuid, setSelectedTicketUuid] = useState<string | null>(null);
  const [editingArea, setEditingArea] = useState<any | null>(null);
  const [showNewArea, setShowNewArea] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);

  const params = useMemo(() => filtersToParams(filters, debouncedSearch), [filters, debouncedSearch]);

  // Cualquier cambio de filtros regresa a la primera página.
  const paramsKey = JSON.stringify(params);
  useEffect(() => { setPage(1); }, [paramsKey]);

  const ticketsQuery = useQuery<any>({
    queryKey: ['tickets', communityId, params, page],
    queryFn: () => api
      .get(`/communities/${communityId}/tickets`, { params: { ...params, page, per_page: PER_PAGE } })
      .then(r => r.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  // Contadores del servidor con los mismos filtros (menos estado), no de la página visible.
  const countsQuery = useQuery<any>({
    queryKey: ['tickets', communityId, 'counts', params],
    queryFn: () => api
      .get(`/communities/${communityId}/tickets/counts`, { params })
      .then(r => r.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  const areasQuery = useQuery<any>({
    queryKey: ['ticket-areas', communityId],
    queryFn: () => api
      .get(`/communities/${communityId}/ticket-areas`)
      .then(r => r.data),
    enabled: !!communityId,
  });

  const categoriesQuery = useQuery<any>({
    queryKey: ['ticket-categories', communityId],
    queryFn: () => api
      .get(`/communities/${communityId}/ticket-categories`)
      .then(r => r.data),
    enabled: !!communityId,
  });

  const tickets: any[] = ticketsQuery.data?.data ?? [];
  const areas: any[] = areasQuery.data ?? [];
  const categories: any[] = categoriesQuery.data ?? [];
  const counts = countsQuery.data ?? { nuevo: 0, en_seguimiento: 0, atendida: 0, total: 0 };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['tickets', communityId] });
    queryClient.invalidateQueries({ queryKey: ['tickets.report', communityId] });
    queryClient.invalidateQueries({ queryKey: ['ticket-areas', communityId] });
    queryClient.invalidateQueries({ queryKey: ['ticket-categories', communityId] });
  };

  const viewOptions = [
    { value: 'tickets', label: 'Todo', count: ticketsQuery.data?.total ?? 0 },
    { value: 'areas', label: 'Áreas', count: areas.length },
    ...(canManage ? [{ value: 'categories', label: 'Categorías', count: categories.length }] : []),
    ...(canSeeCommunity ? [{ value: 'reports', label: 'Reportes' }] : []),
  ];

  const showFilters = view === 'tickets' || view === 'reports';

  return (
    <View style={tw`flex-1`}>
      {/* ===== Header: cards de resumen + tabs ===== */}
      <View style={tw`flex-row items-stretch mb-4 gap-4`}>
        <View style={tw`flex-1 flex-row items-center`}>
          <Illustration name="reportes" width={40} height={40} style={tw`mr-3`} />
          <View>
            <Text style={tw`text-secondary font-600 text-base mb-1`}>Reportes y solicitudes</Text>
            <View style={tw`self-start bg-white border border-gray-200 rounded-full px-3 py-1`}>
              <Text style={tw`text-gray-500 text-sm`}>{dayjs().format('dddd D [de] MMMM')}</Text>
            </View>
          </View>
        </View>

        <SummaryStatCard
          iconName="incidenciaNueva"
          border="border-red"
          labelColor="text-red"
          value={counts.nuevo}
          label="NUEVAS"
        />
        <SummaryStatCard
          iconName="incidenciaProceso"
          border="border-amber-400"
          labelColor="text-amber-600"
          value={counts.en_seguimiento}
          label="EN SEGUIMIENTO"
        />
        <SummaryStatCard
          iconName="incidenciaResuelta"
          border="border-primary"
          labelColor="text-primary"
          value={counts.atendida}
          label="ATENDIDAS"
        />
      </View>

      {/* ===== Toolbar ===== */}
      <View style={tw`bg-white rounded-2xl border border-gray-200 px-5 py-3 mb-4 flex-row items-center justify-between`}>
        <View style={tw`flex-row items-center flex-1`}>
          <View style={tw`flex-row items-center bg-gray-50 border border-gray-200 rounded-full px-4 py-2 mr-3 w-64`}>
            <Ionicons name="search" size={16} color="#9ca3af" />
            <TextInput
              value={filters.search}
              onChangeText={(search) => setFilters(f => ({ ...f, search }))}
              placeholder="Buscar por folio o descripción"
              placeholderTextColor="#9ca3af"
              style={tw`ml-2 flex-1 text-sm text-gray-900`}
            />
          </View>

          <TouchableOpacity
            onPress={() => setShowNewTicket(true)}
            style={tw`bg-secondary px-5 py-2 rounded-full mr-3`}
          >
            <Text style={tw`text-white font-500 text-sm`}>Crear nuevo reporte</Text>
          </TouchableOpacity>

          {view === 'tickets' && <ExcelButton />}
        </View>

        <SegmentedControl
          value={view}
          onChange={(v: ViewMode) => setView(v)}
          options={viewOptions}
        />
      </View>

      {/* ===== Contenido ===== */}
      <View style={tw`flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden`}>
        {showFilters && (
          <TicketFiltersBar
            communityId={communityId}
            filters={filters}
            onChange={setFilters}
            areas={areas}
            categories={categories}
            canSeeCommunity={canSeeCommunity}
          />
        )}

        {view === 'tickets' ? (
          <TicketsTable
            tickets={tickets}
            hasCategories={categories.length > 0}
            isLoading={ticketsQuery.isLoading}
            pagination={ticketsQuery.data}
            onPageChange={setPage}
            onRowPress={(t: any) => setSelectedTicketUuid(t.uuid)}
          />
        ) : view === 'areas' ? (
          <AreasGrid
            areas={areas}
            isLoading={areasQuery.isLoading}
            onEditArea={setEditingArea}
            onCreateArea={() => setShowNewArea(true)}
          />
        ) : view === 'categories' ? (
          <CategoriesGrid
            categories={categories}
            isLoading={categoriesQuery.isLoading}
            onEditCategory={setEditingCategory}
            onCreateCategory={() => setShowNewCategory(true)}
          />
        ) : (
          <TicketsReport communityId={communityId} params={params} />
        )}
      </View>

      {/* ===== Modales ===== */}
      {showNewTicket && (
        <NewTicketModal
          communityId={communityId}
          areas={areas}
          categories={categories}
          onRequestClose={() => setShowNewTicket(false)}
          onCreated={() => { setShowNewTicket(false); invalidateAll(); }}
        />
      )}
      {selectedTicketUuid && (
        <TicketDetailModal
          communityId={communityId}
          ticketUuid={selectedTicketUuid}
          areas={areas}
          categories={categories}
          onRequestClose={() => setSelectedTicketUuid(null)}
          onChanged={invalidateAll}
        />
      )}
      {(editingArea || showNewArea) && (
        <AreaEditModal
          communityId={communityId}
          area={editingArea}
          onRequestClose={() => { setEditingArea(null); setShowNewArea(false); }}
          onSaved={() => { setEditingArea(null); setShowNewArea(false); invalidateAll(); }}
        />
      )}
      {(editingCategory || showNewCategory) && (
        <CategoryEditModal
          communityId={communityId}
          category={editingCategory}
          areas={areas}
          onRequestClose={() => { setEditingCategory(null); setShowNewCategory(false); }}
          onSaved={() => { setEditingCategory(null); setShowNewCategory(false); invalidateAll(); }}
        />
      )}
    </View>
  );
}


// ============================================================
// Sub-componentes
// ============================================================

function SummaryStatCard({ iconName, border, labelColor, value, label }: any) {
  return (
    <View style={tw.style(`flex-row items-center bg-white rounded-2xl px-5 py-3 border`, border)}>
      <Icon name={iconName} size={40} />
      <View style={tw`ml-3`}>
        <Text style={tw`text-2xl font-700 text-gray-900`}>{value}</Text>
        <Text style={tw.style(`text-xs font-500 tracking-wide`, labelColor)}>{label}</Text>
      </View>
    </View>
  );
}

function SegmentedControl({ value, onChange, options }: any) {
  // Segmented sobre fondo blanco (track #F3F2F3, pill blanco activo, texto morado).
  return (
    <View style={tw`flex-row bg-[#F3F2F3] rounded-xl p-1 self-start`}>
      {options.map((opt: any) => {
        const isActive = value === opt.value;
        return (
          <TouchableOpacity
            key={opt.value}
            onPress={() => onChange(opt.value)}
            style={tw.style(`flex-row items-center justify-center rounded-lg px-5 py-2`, isActive && `bg-white shadow-sm`)}
          >
            <Text style={tw.style(`text-sm`, isActive ? `text-secondary font-600` : `text-gray-400 font-500`)}>
              {opt.label}
            </Text>
            {opt.count !== undefined && (
              <View style={tw.style(`ml-2 px-2 py-0.5 rounded-full`, isActive ? `bg-secondary-50` : `bg-gray-200`)}>
                <Text style={tw.style(`text-xs font-600`, isActive ? `text-secondary` : `text-gray-500`)}>{opt.count}</Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function TicketsTable({ tickets, hasCategories, isLoading, pagination, onPageChange, onRowPress }: any) {
  return (
    <>
      {/* Header de tabla */}
      <View style={tw`flex-row px-5 py-3 border-b border-gray-200/70 bg-gray-50`}>
        <Text style={tw`w-30 text-xs font-500 text-gray-500`}>Folio/fecha</Text>
        <Text style={tw`w-40 text-xs font-500 text-gray-500`}>Área</Text>
        {hasCategories && <Text style={tw`w-36 text-xs font-500 text-gray-500`}>Categoría</Text>}
        <Text style={tw`w-36 text-xs font-500 text-gray-500`}>Estatus</Text>
        <Text style={tw`flex-1 text-xs font-500 text-gray-500`}>Solicitud</Text>
        <Text style={tw`w-20 text-xs font-500 text-gray-500 text-right`}>Evidencia</Text>
      </View>

      {/* Filas */}
      {isLoading && tickets.length === 0 ? (
        <View style={tw`px-5 py-8 items-center`}>
          <ActivityIndicator size="small" color="#6366f1" />
          <Text style={tw`text-gray-400 mt-2`}>Cargando…</Text>
        </View>
      ) : tickets.length === 0 ? (
        <View style={tw`py-12 items-center`}>
          <Ionicons name="alert-circle-outline" size={48} color="#d1d5db" />
          <Text style={tw`text-gray-400 mt-3`}>No hay reportes ni solicitudes</Text>
        </View>
      ) : (
        <ScrollView style={tw`flex-1`}>
          {tickets.map((t: any) => (
            <TicketRow key={t.uuid} ticket={t} hasCategories={hasCategories} onPress={() => onRowPress(t)} />
          ))}
        </ScrollView>
      )}

      <Pagination pagination={pagination} onPageChange={onPageChange} />
    </>
  );
}

function Pagination({ pagination, onPageChange }: any) {
  if (!pagination || pagination.total === 0 || pagination.last_page <= 1) return null;

  const { current_page, last_page, from, to, total } = pagination;

  return (
    <View style={tw`flex-row items-center justify-between px-5 py-3 border-t border-gray-200/70`}>
      <Text style={tw`text-xs text-gray-500`}>{from}–{to} de {total}</Text>
      <View style={tw`flex-row items-center gap-2`}>
        <PageButton icon="chevron-back" disabled={current_page <= 1} onPress={() => onPageChange(current_page - 1)} />
        <Text style={tw`text-xs text-gray-600`}>Página {current_page} de {last_page}</Text>
        <PageButton icon="chevron-forward" disabled={current_page >= last_page} onPress={() => onPageChange(current_page + 1)} />
      </View>
    </View>
  );
}

function PageButton({ icon, disabled, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={tw.style(`w-8 h-8 rounded-full border items-center justify-center`, disabled ? 'border-gray-100' : 'border-gray-200')}
    >
      <Ionicons name={icon} size={14} color={disabled ? '#d1d5db' : '#374151'} />
    </TouchableOpacity>
  );
}

function TicketRow({ ticket, hasCategories, onPress }: any) {
  const reporterRoleLabel = ticket.reporter_role === 'admin' ? 'Administración'
    : ticket.reporter_role === 'security' ? 'Vigilancia' : 'Residente';
  const reporterRoleColor = ticket.reporter_role === 'admin' ? 'text-secondary'
    : ticket.reporter_role === 'security' ? 'text-emerald-700' : 'text-primary';

  const evidenceUrl = ticket.files?.[0]?.url;
  const isHigh = !!ticket.is_high_priority;

  return (
    <TouchableOpacity onPress={onPress} style={tw`flex-row items-center px-5 py-3 border-b border-gray-200/70 hover:bg-gray-50`}>
      <View style={tw`w-30`}>
        <Text style={tw`text-sm text-gray-900`}>{ticket.folio}</Text>
        <Text style={tw`text-xs text-gray-500`}>{dayjs(ticket.created_at).format('D MMM · H:mm')}</Text>
      </View>

      <View style={tw`w-40 pr-2`}>
        {ticket.area ? <AreaChip area={ticket.area} /> : (
          <Text style={tw`text-sm text-red`}>Por asignar</Text>
        )}
      </View>

      {hasCategories && (
        <View style={tw`w-36 pr-2`}>
          {ticket.category ? <AreaChip area={ticket.category} /> : (
            <Text style={tw`text-sm text-gray-300`}>—</Text>
          )}
        </View>
      )}

      <View style={tw`w-36 pr-2 flex-row items-center`}>
        {isHigh && (
          <Text style={tw`text-red text-sm font-700 mr-1.5`}>!!</Text>
        )}
        <StatusPill status={ticket.status} label={ticket.status_str} />
      </View>

      <View style={tw`flex-1 pr-3`}>
        <View style={tw`flex-row items-center`}>
          <Text style={tw.style(`text-sm font-500 mr-2`, reporterRoleColor)}>{reporterRoleLabel}</Text>
          <Text style={tw`text-sm text-gray-700`} numberOfLines={1}>
            {ticket.reporter?.name}
            {ticket.unit?.name ? <Text style={tw`text-gray-500`}> - {ticket.unit.name}</Text> : null}
          </Text>
          <DebtBadge status={ticket.unit?.debt_status} showAmount={false} style={tw`ml-2`} />
        </View>
        <Text style={tw`text-xs text-gray-500 mt-0.5`} numberOfLines={1}>
          {ticket.description ?? ''}
        </Text>
      </View>

      <View style={tw`w-20 items-end`}>
        {evidenceUrl ? (
          <RNImage source={{ uri: evidenceUrl }} style={tw`w-9 h-9 rounded`} />
        ) : (
          <View style={tw`w-9 h-9 bg-gray-200 rounded`} />
        )}
      </View>
    </TouchableOpacity>
  );
}

function AreasGrid({ areas, isLoading, onEditArea, onCreateArea }: any) {
  if (isLoading) {
    return (
      <View style={tw`px-5 py-8 items-center`}>
        <ActivityIndicator size="small" color="#6366f1" />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={tw`p-5`}>
      <Text style={tw`text-base font-600 text-secondary mb-1`}>Áreas del fraccionamiento</Text>
      <Text style={tw`text-sm text-gray-500 mb-5`}>
        Cada área tiene un responsable que recibe las incidencias asignadas. Puedes crear las áreas que tu fraccionamiento necesite.
      </Text>

      <View style={tw`flex-row flex-wrap gap-4`}>
        {areas.map((a: any) => (
          <AreaCard key={a.uuid} area={a} onEdit={() => onEditArea(a)} />
        ))}

        <TouchableOpacity
          onPress={onCreateArea}
          style={tw`w-64 h-36 rounded-2xl border-2 border-dashed border-secondary items-center justify-center`}
        >
          <Ionicons name="add" size={24} color="#9ca3af" />
          <Text style={tw`text-sm text-gray-600 mt-1`}>Agregar nueva Área</Text>
          <Text style={tw`text-xs text-gray-400 mt-1 px-3 text-center`}>
            Crea las áreas del fraccionamiento (Casa Club, gym, alberca…)
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

function AreaCard({ area, onEdit }: any) {
  const initials = area.name?.slice(0, 2)?.toUpperCase() ?? '??';
  return (
    <View style={tw`w-64 rounded-2xl border border-gray-200 p-4 bg-white`}>
      <View style={tw`flex-row items-center`}>
        <View style={tw.style(`w-10 h-10 rounded items-center justify-center`, { backgroundColor: area.color + '33' })}>
          <Text style={tw.style(`text-xs font-600`, { color: area.color })}>{initials}</Text>
        </View>
        <View style={tw`ml-3 flex-1`}>
          <Text style={tw`text-sm font-600 text-secondary`}>{area.name}</Text>
          <Text style={tw`text-sm text-gray-700`} numberOfLines={1}>
            {area.responsibleUser?.name ?? area.responsible_user?.name ?? 'Sin responsable'}
          </Text>
        </View>
      </View>

      <View style={tw`flex-row items-center justify-between mt-4 pt-3 border-t border-gray-100`}>
        <Text style={tw`text-sm`}>
          <Text style={tw`font-700 text-gray-900`}>{area.tickets_count ?? 0}</Text>
          <Text style={tw`text-gray-500`}> Asignadas</Text>
        </Text>
        <TouchableOpacity onPress={onEdit} style={tw`flex-row items-center px-3 py-1.5 border border-gray-200 rounded-lg`}>
          <Text style={tw`text-xs text-gray-700 mr-1`}>Editar</Text>
          <Ionicons name="create-outline" size={14} color="#374151" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function CategoriesGrid({ categories, isLoading, onEditCategory, onCreateCategory }: any) {
  if (isLoading) {
    return (
      <View style={tw`px-5 py-8 items-center`}>
        <ActivityIndicator size="small" color="#6366f1" />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={tw`p-5`}>
      <Text style={tw`text-base font-600 text-secondary mb-1`}>Categorías de reportes</Text>
      <Text style={tw`text-sm text-gray-500 mb-5`}>
        La categoría dice qué pasó (fuga, alumbrado, ruido…); el área, quién lo atiende. Si una categoría tiene área por default, los reportes se asignan solos a esa área. Son opcionales: sin categorías, no se le piden a nadie.
      </Text>

      <View style={tw`flex-row flex-wrap gap-4`}>
        {categories.map((c: any) => (
          <CategoryCard key={c.uuid} category={c} onEdit={() => onEditCategory(c)} />
        ))}

        <TouchableOpacity
          onPress={onCreateCategory}
          style={tw`w-64 h-32 rounded-2xl border-2 border-dashed border-secondary items-center justify-center`}
        >
          <Ionicons name="add" size={24} color="#9ca3af" />
          <Text style={tw`text-sm text-gray-600 mt-1`}>Agregar categoría</Text>
          <Text style={tw`text-xs text-gray-400 mt-1 px-3 text-center`}>
            Ej. Alumbrado, fuga de agua, ruido, mascotas…
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

function CategoryCard({ category, onEdit }: any) {
  const defaultArea = category.default_area ?? category.defaultArea;
  return (
    <View style={tw`w-64 rounded-2xl border border-gray-200 p-4 bg-white`}>
      <View style={tw`flex-row items-center`}>
        <View style={tw.style(`w-3 h-3 rounded-full`, { backgroundColor: category.color })} />
        <View style={tw`ml-3 flex-1`}>
          <Text style={tw`text-sm font-600 text-secondary`}>{category.name}</Text>
          <Text style={tw`text-xs text-gray-500`} numberOfLines={1}>
            {defaultArea ? `Se asigna a ${defaultArea.name}` : 'Sin área por default'}
          </Text>
        </View>
      </View>

      <View style={tw`flex-row items-center justify-between mt-4 pt-3 border-t border-gray-100`}>
        <Text style={tw`text-sm`}>
          <Text style={tw`font-700 text-gray-900`}>{category.tickets_count ?? 0}</Text>
          <Text style={tw`text-gray-500`}> Reportes</Text>
        </Text>
        <TouchableOpacity onPress={onEdit} style={tw`flex-row items-center px-3 py-1.5 border border-gray-200 rounded-lg`}>
          <Text style={tw`text-xs text-gray-700 mr-1`}>Editar</Text>
          <Ionicons name="create-outline" size={14} color="#374151" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

export function StatusPill({ status, label }: any) {
  const color =
    status === 'nuevo' ? { bg: 'bg-red/10', text: 'text-red' } :
    status === 'en_seguimiento' ? { bg: 'bg-amber-50', text: 'text-amber-600' } :
    { bg: 'bg-primary-50', text: 'text-primary' };
  return (
    <Text style={tw.style(`px-2 py-0.5 rounded-full text-xs font-500 self-start`, color.bg, color.text)}>
      {label}
    </Text>
  );
}

export function PriorityPill({ isHigh }: { isHigh: boolean }) {
  if (!isHigh) {
    return <Text style={tw`text-xs text-gray-300`}>—</Text>;
  }
  return (
    <Text style={tw.style(`px-2 py-0.5 rounded-full text-xs font-500 self-start bg-red-50 text-red-600`)}>
      Alta
    </Text>
  );
}

export function AreaChip({ area }: any) {
  if (!area) return null;
  return (
    <View style={tw.style(`px-2 py-1 rounded-full self-start`, { backgroundColor: (area.color ?? '#7C3AED') + '20' })}>
      <Text style={tw.style(`text-xs font-500`, { color: area.color ?? '#7C3AED' })}>{area.name}</Text>
    </View>
  );
}
